import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowRight } from "react-feather";
import { getActionRoute } from "./nextStepCopy";
import { recordGuidanceInteraction } from "@src/redux/actions/brandsDashboard";

const CelebrationIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 3L13.8 8.2L19 10L13.8 11.8L12 17L10.2 11.8L5 10L10.2 8.2L12 3Z"
      fill="#56B6FF"
    />
    <circle cx="18.5" cy="5.5" r="1.5" fill="#56B6FF" />
    <circle cx="6" cy="18" r="1.5" fill="#56B6FF" />
    <circle cx="18" cy="18" r="1.5" fill="#56B6FF" />
  </svg>
);

const BrandAnnouncement = ({ announcement, notification, recentApplicant, recentApplicants, isLoading }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [announcementDismissed, setAnnouncementDismissed] = useState(false);
  const [notificationDismissed, setNotificationDismissed] = useState(false);

  // Synchronize with admin published announcements in localStorage
  const [storedAnnouncement, setStoredAnnouncement] = useState(() => {
    try {
      const stored = localStorage.getItem("rdlink_admin_announcement");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  });

  // Track the ID of the last dismissed announcement
  const [dismissedId, setDismissedId] = useState(() => {
    try {
      return localStorage.getItem("rdlink_last_dismissed_announcement_id");
    } catch (e) {
      return null;
    }
  });

  const isNotificationDismissed = (notifId) => {
    if (!notifId) return false;
    try {
      return localStorage.getItem(`rdlink_notification_dismissed_${notifId}`) === "true";
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    const handleStorageChange = (e) => {
      try {
        if (!e || !e.key || e.key === "rdlink_admin_announcement" || e.key === "rdlink_admin_announcement_timestamp") {
          const stored = localStorage.getItem("rdlink_admin_announcement");
          setStoredAnnouncement(stored ? JSON.parse(stored) : null);
          setAnnouncementDismissed(false);
          const currentDismissed = localStorage.getItem("rdlink_last_dismissed_announcement_id");
          setDismissedId(currentDismissed);
        }
      } catch (err) { }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Backend projection is the source of truth.
  // Old locally stored announcements must NEVER appear when the backend returns no active announcement.
  const activeAnnouncement = (() => {
    // 1. If backend explicitly returns null or false: there is NO active announcement.
    if (announcement === null || announcement === false) {
      return null;
    }
    // 2. If backend returns an announcement object:
    if (announcement && typeof announcement === "object") {
      const isActive = announcement.is_active !== false && announcement.active !== false;
      return isActive && announcement.body ? announcement : null;
    }
    // 3. If announcement prop is undefined and backend has loaded, backend has no active announcement
    if (announcement === undefined && !isLoading) {
      return null;
    }
    // 4. Fallback only when announcement prop is not provided (e.g. standalone test/preview)
    return (storedAnnouncement && (storedAnnouncement.is_active || storedAnnouncement.active) && storedAnnouncement.body)
      ? storedAnnouncement
      : null;
  })();

  // Whenever active announcement ID changes, reset local dismissal so new announcement shows
  useEffect(() => {
    setAnnouncementDismissed(false);
  }, [activeAnnouncement?.id]);

  // Candidate applicant: check recentApplicants list for the first undismissed applicant
  const candidateApplicant = (() => {
    const list = Array.isArray(recentApplicants) && recentApplicants.length
      ? recentApplicants
      : (recentApplicant ? [recentApplicant] : []);

    for (const app of list) {
      if (!app) continue;
      const rdName = app.rd_name || app.name;
      const campaignTitle = app.campaign_title || app.title;
      if (!rdName || !campaignTitle) continue;
      const uniqueAppKey = `applicant_${app.campaign_id || app.campaignId || "c"}_${app.rd_id || app.id || "r"}_${app.applied_at || ""}`;
      if (!isNotificationDismissed(uniqueAppKey)) {
        return { ...app, uniqueAppKey };
      }
    }
    return null;
  })();

  const activeNotification =
    notification !== undefined && notification !== null
      ? notification
      : (candidateApplicant
        ? {
          id: candidateApplicant.uniqueAppKey,
          message: `${candidateApplicant.rd_name || candidateApplicant.name} joined your ${candidateApplicant.campaign_title || candidateApplicant.title} — ready to collaborate.`,
          cta: {
            type: "open_messages",
            action: "open_messages",
            rd_id: candidateApplicant.rd_id || candidateApplicant.id
          },
          dismissible: true
        }
        : null);

  // Whenever activeNotification ID changes (e.g. a new applicant arrives), reset local dismissed state!
  useEffect(() => {
    setNotificationDismissed(false);
  }, [activeNotification?.id]);

  if (isLoading) return null;

  // Render announcement check: active and within scheduled dates
  const isScheduledActive = () => {
    if (!activeAnnouncement || !activeAnnouncement.body) return false;
    if (activeAnnouncement.active === false || activeAnnouncement.is_active === false) return false;
    const now = Date.now();
    const startDate = activeAnnouncement.start_date || activeAnnouncement.starts_at;
    if (startDate) {
      const start = new Date(startDate).getTime();
      if (!isNaN(start) && now < start) return false;
    }
    const endDate = activeAnnouncement.end_date || activeAnnouncement.ends_at;
    if (endDate) {
      const end = new Date(endDate).getTime();
      if (!isNaN(end) && now > end) return false;
    }
    return true;
  };

  const handleDismissAnnouncement = () => {
    setAnnouncementDismissed(true);
    if (activeAnnouncement?.id) {
      setDismissedId(String(activeAnnouncement.id));
      try {
        localStorage.setItem("rdlink_last_dismissed_announcement_id", String(activeAnnouncement.id));
      } catch (e) { }
      dispatch(
        recordGuidanceInteraction({
          card_id: `announcement:${activeAnnouncement.id}`,
          event: "dismissed"
        })
      );
    }
  };

  const handleAnnouncementCta = (cta) => {
    if (!cta) return;
    if (activeAnnouncement?.id) {
      dispatch(
        recordGuidanceInteraction({
          card_id: `announcement:${activeAnnouncement.id}`,
          event: "opened"
        })
      );
    }
    if (cta.type === "external" || (cta.url && cta.type !== "internal")) {
      if (cta.url && (cta.url.startsWith("http://") || cta.url.startsWith("https://"))) {
        window.open(cta.url, "_blank", "noopener,noreferrer");
      } else if (cta.url) {
        history.push(cta.url);
      }
    } else if (cta.action) {
      history.push(getActionRoute({ type: cta.action }));
    }
  };

  const handleNotificationCta = (cta) => {
    if (!cta) return;
    if (activeNotification?.id) {
      dispatch(
        recordGuidanceInteraction({
          card_id: `notification:${activeNotification.id}`,
          event: "opened"
        })
      );
    }
    const targetRdId = cta.rd_id || cta.rdId || candidateApplicant?.rd_id || candidateApplicant?.id;
    if (targetRdId) {
      try {
        localStorage.setItem(
          'selectedChatRd',
          JSON.stringify({
            id: targetRdId,
            chat_user: targetRdId,
            name: candidateApplicant?.rd_name || candidateApplicant?.name || "",
            rd_name: candidateApplicant?.rd_name || candidateApplicant?.name || "",
            profile_image: candidateApplicant?.rd_avatar_url || candidateApplicant?.profile_image || ""
          })
        );
      } catch (e) { }
    }
    history.push(
      getActionRoute({
        type: cta.action || cta.type || "open_messages",
        rd_id: targetRdId
      })
    );
  };

  const handleDismissNotification = () => {
    setNotificationDismissed(true);
    if (activeNotification?.id) {
      try {
        localStorage.setItem(`rdlink_notification_dismissed_${activeNotification.id}`, "true");
      } catch (e) { }
      dispatch(
        recordGuidanceInteraction({
          card_id: `notification:${activeNotification.id}`,
          event: "dismissed"
        })
      );
    }
  };

  const renderNotificationMessage = () => {
    if (!activeNotification) return null;
    const msg = activeNotification.message || "";
    if (msg.includes(" — ")) {
      const parts = msg.split(" — ");
      return (
        <>
          <strong className="notif-headline-bold">{parts[0]}</strong>
          <span className="notif-subtext"> — {parts.slice(1).join(" — ")} </span>
        </>
      );
    }
    return <strong className="notif-headline-bold">{msg} </strong>;
  };

  const adminDismissibleSetting = (() => {
    try {
      const setting = localStorage.getItem("rdlink_admin_announcement_dismissible");
      if (setting !== null) return setting === "true";
      const stored = localStorage.getItem("rdlink_admin_announcement");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.dismissible !== undefined) return Boolean(parsed.dismissible);
      }
    } catch (e) { }
    return true;
  })();

  const isAnnouncementDismissible = Boolean(
    activeAnnouncement?.dismissible === true ||
    activeAnnouncement?.is_dismissible === true ||
    adminDismissibleSetting
  );

  // Announcement is considered dismissed only if the current announcement ID was specifically dismissed
  const isAnnouncementDismissed = Boolean(
    announcementDismissed ||
    (dismissedId && activeAnnouncement?.id && String(activeAnnouncement.id) === String(dismissedId))
  );

  const shouldRenderAnnouncement =
    isScheduledActive() &&
    (!isAnnouncementDismissible || !isAnnouncementDismissed);

  return (
    <>
      {/* ─── 1. Admin Announcement Card (Black Box) ─── */}
      {shouldRenderAnnouncement && (
        <div className="figma-admin-banner" id="brand-admin-announcement">
          <div className="banner-left-avatar-circle" />

          <div className="banner-center-content">
            <div className="banner-meta-row">
              <span className="banner-source-tag">FROM THE RD LINK</span>
              <span className="banner-admin-pill">Admin</span>
            </div>

            {activeAnnouncement.title ? (
              <h5 className="banner-title text-white font-weight-bold mb-50">
                {activeAnnouncement.title}
              </h5>
            ) : null}

            <p className="banner-body-text" style={{ whiteSpace: "pre-line" }}>
              {activeAnnouncement.body}
            </p>

            {activeAnnouncement.cta && activeAnnouncement.cta.label ? (
              <div className="banner-cta-container mt-1">
                <button
                  type="button"
                  className="btn btn-sm btn-primary banner-cta-btn"
                  onClick={() => handleAnnouncementCta(activeAnnouncement.cta)}
                >
                  <span>{activeAnnouncement.cta.label}</span>
                  <ArrowRight size={14} className="ml-50" />
                </button>
              </div>
            ) : null}
          </div>

          <div className="banner-right-action">
            {isAnnouncementDismissible && (
              <button
                type="button"
                className="banner-dismiss-link text-white-50"
                onClick={handleDismissAnnouncement}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── 2. Inline Notification Strip (Below Black Box) ─── */}
      {activeNotification && !notificationDismissed && !isNotificationDismissed(activeNotification?.id) && (
        <div className="figma-notification-banner" id="brand-inline-notification">
          <div className="notif-left-icon-circle">
            <CelebrationIcon />
          </div>

          <div className="notif-center-content">
            <span className="notif-text">
              {renderNotificationMessage()}
              <button
                type="button"
                className="btn btn-link p-0 notif-say-hello-link"
                onClick={() => handleNotificationCta(activeNotification.cta)}
              >
                <span className="say-hello-text">Say hello</span>
                <ArrowRight size={14} className="ml-25 say-hello-arrow" />
              </button>
            </span>
          </div>

          <div className="notif-right-action">
            {activeNotification.dismissible !== false && (
              <button
                type="button"
                className="notif-dismiss-link"
                onClick={handleDismissNotification}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

BrandAnnouncement.propTypes = {
  announcement: PropTypes.object,
  notification: PropTypes.object,
  recentApplicant: PropTypes.object,
  recentApplicants: PropTypes.array,
  isLoading: PropTypes.bool
};

export default BrandAnnouncement;
